import axios from 'axios';

// Shared by the public mini tools. They run inside an iframe on the marketing site, so everything
// goes through the Next api routes on this origin rather than straight to the api.

// Every meaningful action a tool can report. Keep in sync with MINI_TOOL_EVENTS on the server.
export const EVENTS = {
  search: 'search',
  select: 'select',
  generate: 'generate',
  copy: 'copy',
  download: 'download',
};

const SESSION_KEY = 'shapo_tool_session';

// the only identifier the tools have. Without it a report cannot tell one visitor running ten
// searches from ten visitors running one, and the search -> pick -> copy funnel cannot be followed
// through a single visit. Storage is partitioned or blocked in plenty of iframe contexts, so a
// per page-load fallback keeps at least the current visit joined up.
let memorySessionId = null;
function getSessionId() {
  if(typeof window === 'undefined') {
    return null;
  }
  if(!memorySessionId) {
    memorySessionId = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
  try {
    const stored = window.localStorage.getItem(SESSION_KEY);
    if(stored) {
      return stored;
    }
    window.localStorage.setItem(SESSION_KEY, memorySessionId);
  } catch(err) {
    return memorySessionId;
  }
  return memorySessionId;
}

// The site the tool is embedded on. The api cannot work this out on its own: an XHR carries the
// Referer of the document that sent it, which for an iframed tool is the tool page, never the page
// around it. Cross origin referrer policy cuts document.referrer down to an origin, so a path or a
// campaign is not available here however it is read.
function getEmbedderOrigin() {
  if(typeof window === 'undefined') {
    return null;
  }
  const ancestors = window.location.ancestorOrigins;
  if(ancestors && ancestors.length) {
    return ancestors[0];
  }
  if(!window.document.referrer) {
    return null;
  }
  const match = window.document.referrer.match(/^(https?:\/\/[^/?#]+)/i);
  return match ? match[1] : null;
}

// Repeated clicks on the same button are the one way these tools can write rows in bulk, and a
// second identical event says nothing the first did not. Held per page load, so a visitor coming
// back later is still counted.
const DEDUPE_MS = 5000;
const lastSent = new Map();
function isRepeat(key) {
  const now = Date.now();
  const previous = lastSent.get(key);
  lastSent.set(key, now);
  return !!previous && now - previous < DEDUPE_MS;
}

// The picked place is what turns a usage row into a lead, the website most of all. Takes the shape
// /api/search-places returns, which is what every tool holds on to.
export function businessFromPlace(place) {
  if(!place) {
    return null;
  }
  return {
    name: place.displayName?.text || null,
    website: place.websiteUri || null,
    address: place.formattedAddress || null,
    rating: place.rating || null,
    reviewCount: place.userRatingCount || null,
  };
}

export async function searchPlaces({ query, turnstileToken, tool }) {
  const response = await axios.post('/api/search-places', {
    textQuery: query,
    turnstileToken,
    tool,
    sessionId: getSessionId(),
    embedderOrigin: getEmbedderOrigin(),
  });
  return response.data.places || [];
}

export async function fetchPlaceReviews({ dataId, turnstileToken }) {
  const response = await axios.post('/api/place-reviews', { dataId, turnstileToken }).catch(() => null);
  return response?.data?.reviews || [];
}

// fire and forget, usage reporting should never be able to break the tool itself
export function trackEvent({
  tool, event, query, placeId, label, business,
}) {
  if(isRepeat([tool, event, label, placeId, business && business.website].join('|'))) {
    return;
  }
  axios.post('/api/tool-event', {
    tool,
    event,
    query,
    placeId,
    label,
    business,
    sessionId: getSessionId(),
    embedderOrigin: getEmbedderOrigin(),
  }).catch(() => {});
}
